import { Smoke } from 'react-smoke';
import { Canvas, useFrame } from '@react-three/fiber';
import { useState, useEffect, useRef, Suspense, useMemo } from 'react';
import * as THREE from 'three';
import { 
    SMOKE_APPEAR_DELAY, 
    SMOKE_OPACITY, 
    SMOKE_DENSITY,
    SMOKE_LIGHT_INTENSITY,
    INTRO_COLORS,
    SMOKE_FADE_DURATION,
    SMOKE_FADE_START_DELAY
} from '../constants';
import React, { memo } from 'react';

function SmokeEffect({ colorScheme = 'FIRST' }) {
    const smokeRef = useRef();
    const startTimeRef = useRef(null);
    const fadeStartTimeRef = useRef(null);
    const smokeColor = useMemo(() => 
        INTRO_COLORS[colorScheme].SMOKE ? new THREE.Color(...INTRO_COLORS[colorScheme].SMOKE) : null,
        [colorScheme]
    );
    const opacityRef = useRef(SMOKE_OPACITY);

    useEffect(() => {
        if (!smokeColor) return;
        
        startTimeRef.current = Date.now();
        // Set timer for fade start
        setTimeout(() => {
            fadeStartTimeRef.current = Date.now();
        }, SMOKE_FADE_START_DELAY);
    }, [smokeColor]);

    // Обновляем цвет дыма при изменении схемы
    useEffect(() => {
        if (!smokeColor || !smokeRef.current) return;

        smokeRef.current.traverse((child) => {
            if (child.material) {
                child.material.color = smokeColor;
                child.material.needsUpdate = true;
            }
        });
    }, [colorScheme, smokeColor]);

    useFrame(() => {
        if (!smokeColor) return;
        
        const now = Date.now();
        
        if (!startTimeRef.current) {
            startTimeRef.current = now;
            return;
        }

        // Only start fading if fade start time is set
        if (fadeStartTimeRef.current) {
            const elapsedTime = now - fadeStartTimeRef.current;
            const fadeProgress = Math.min(elapsedTime / SMOKE_FADE_DURATION, 1);
            opacityRef.current = SMOKE_OPACITY * (1 - fadeProgress);

            if (smokeRef.current) {
                smokeRef.current.traverse((child) => {
                    if (child.material) {
                        child.material.opacity = opacityRef.current;
                        child.material.transparent = true;
                        child.material.needsUpdate = true;
                    }
                });
            }
        }
    });

    // Если SMOKE равен null, не рендерим дым
    if (!smokeColor) return null;

    return (
        <group ref={smokeRef}>
            <Smoke
                opacity={SMOKE_OPACITY}
                density={SMOKE_DENSITY}
                size={[2000, 2000, 2000]}
                enableWind
                windStrength={[0.01, 0.01, 0.01]}
                windDirection={[1, 0, 0]}
                enableRotation
                rotation={[0, 0, 0.1]}
                maxBounds={[800, 800, 800]}
                minBounds={[-800, -800, -800]}
                maxVelocity={[0.1, 0.1, 0.1]}
                color={smokeColor}
                transparent={true}
            />
        </group>
    );
}

const Smoker = ({ colorScheme = 'FIRST' }) => {
    const [showSmoke, setShowSmoke] = useState(false);

    useEffect(() => {
        if (!INTRO_COLORS[colorScheme].SMOKE) return;

        const timer = setTimeout(() => {
            setShowSmoke(true);
        }, SMOKE_APPEAR_DELAY);
        return () => clearTimeout(timer);
    }, [colorScheme]);

    // Если SMOKE равен null, не рендерим компонент вообще
    if (!INTRO_COLORS[colorScheme].SMOKE) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            opacity: showSmoke ? 1 : 0,
            transition: 'opacity 0.5s ease-in'
        }}>
            <Canvas
                camera={{ position: [0, 0, 1000], fov: 75 }}
                onCreated={({ gl }) => {
                    gl.setClearColor('#000000', 0);
                }}
                gl={{ 
                    alpha: true,
                    antialias: true,
                    preserveDrawingBuffer: true
                }}
            >
                <Suspense fallback={null}>
                    <ambientLight intensity={SMOKE_LIGHT_INTENSITY} />
                    <pointLight position={[10, 10, 10]} intensity={SMOKE_LIGHT_INTENSITY} />
                    {showSmoke && <SmokeEffect colorScheme={colorScheme} />}
                </Suspense>
            </Canvas>
        </div>
    );
}

export default memo(Smoker); 